import { useMemo, useState } from 'react'
import { Button, FormTable } from '@fullscreendigital/carrefour-dex-fe'
import { Modal } from 'components/core-ui/Modal'
import { useFormik } from 'formik'
import { getValidationSchema } from './helpers'
import { useNotification } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { Box, CircularProgress } from '@mui/material'
import { makeCoreGetRequest, makeCorePostRequest } from 'utils/api'
import { useMutation, useQuery } from 'react-query'
import { toDetailsTableRows } from "../serializers";
import { buildColumns } from "../helpers";

interface EditStoreProps {
  isOpen: boolean
  closeModal: Function
  setShouldRefreshData: Function
  editItem?: any
}

const EditStore: React.FC<EditStoreProps> = props => {
  const { isOpen, closeModal, editItem, setShouldRefreshData } = props
  const { showNotification } = useNotification()
  const [rows, setRows] = useState([])
  const [shopName, setShopName] = useState('')
  const [depositList, setDepositList] = useState([])
  const [hubList, setHubList] = useState([])

  const { data: shopsFormData, isLoading: isLoadingShopsFormData } = useQuery(
    ['shops-form', editItem],
    async () => {
      const data = await makeCoreGetRequest(
        `/shop-reception/delivery-matrix/shops/${editItem?.id}/form-data`
      )
      setShopName(editItem?.shopName)
      setDepositList(data?.depositList.map(item => ({
        label: item.name,
        value: item.id
      })))
      setHubList(data?.receptionHubList.map(item => ({
        label: item.name,
        value: item.id
      })))

      const deliveryMatrixDetailsRows = toDetailsTableRows(data?.shopDeliveryMatrixDepositDetailsModelList)
      setRows(deliveryMatrixDetailsRows)
      await formik.setFieldValue('deliveryMatrixItems', deliveryMatrixDetailsRows)

      return {
        shopDeliveryMatrixDepositDetailsModelList: data?.shopDeliveryMatrixDepositDetailsModelList,
      }
    },
    {
      onError: error => {
        showNotification({
          message: `A apărut o eroare la obtinerea centralizarii. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      },
      enabled: typeof editItem !== 'undefined' && editItem !== null
    }
  )

  const mutateStore = async values => {
    await makeCorePostRequest(
      `/shop-reception/delivery-matrix/shops/${editItem?.id}`,
      values
    )
  }

  const { isLoading, mutate } = useMutation(mutateStore, {
    onSuccess: () => {
      setShouldRefreshData(true)
      formik.resetForm()
      closeModal()
      showNotification({
        message: `Datele au fost modificate cu succes.`,
        type: 'success'
      })
    },
    onError: res => {
      showNotification({
        message: `A aparut o eroare la modificarea datelor. ${formatErrorMessage(res)}`,
        type: 'error'
      })
    }
  })

  const validationSchema = useMemo(() => getValidationSchema(editItem), [editItem])

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      deliveryMatrixItems: []
    },
    validationSchema: validationSchema,
    onSubmit: async values => {
      const payload = {
        shopDeliveryMatrixDepositDetailsModelList: values.deliveryMatrixItems,
        depositIdList: [],
        hubIdList: []
      }
      mutate(payload)
    }
  })

  const toggleAcceptReturn = row => {
    const newDeliveryMatrixDetails = formik.values.deliveryMatrixItems.map(item =>
        Number(item.deliveryMatrixIndex) === Number(row.deliveryMatrixIndex)
            ? { ...item, acceptReturns: !item.acceptReturns }
            : item
    )
    formik.setFieldValue('deliveryMatrixItems', newDeliveryMatrixDetails)
    setRows(newDeliveryMatrixDetails)
  }

  //DIRECT or PAXD or HUB
  const toggleDeliveryType = (row, deliveryType) => {
    const newDeliveryMatrixDetails = formik.values.deliveryMatrixItems.map(item =>
      Number(item.deliveryMatrixIndex) === Number(row.deliveryMatrixIndex)
        ? { ...item, deliveryMatrixType: deliveryType }
        : item
    )
    formik.setFieldValue('deliveryMatrixItems', newDeliveryMatrixDetails)
    setRows(newDeliveryMatrixDetails)
  }

  const toggleDeliveryDeposit = (row, checked) => {
    const newDeliveryMatrixDetails = (checked)
      ? formik.values.deliveryMatrixItems.map(item =>
        +item.deliveryMatrixIndex === +row.deliveryMatrixIndex
          ? { ...item, deposit: item.deliveryDeposit }
          : item)
      : formik.values.deliveryMatrixItems.map(item =>
        +item.deliveryMatrixIndex === +row.deliveryMatrixIndex
          ? { ...item, deposit: null, paxdDeposit: null, receptionHub: null, acceptReturns: false }
          : item)

    formik.setFieldValue('deliveryMatrixItems', newDeliveryMatrixDetails)
    setRows(newDeliveryMatrixDetails)
  }

  const columns = useMemo(
      () => buildColumns({
        formik,
        toggleDeliveryDeposit: toggleDeliveryDeposit,
        toggleDeliveryType: toggleDeliveryType,
        toggleAcceptReturns: toggleAcceptReturn,
        depositList: depositList,
        hubList: hubList }),
      [rows, formik.errors]
  )

  return (
    <Modal
      isModalOpen={isOpen}
      closeModal={closeModal}
      modalTitle={`Editează matrice magazin ${shopName}`}
      modalWidth="80%"
    >

      {isLoadingShopsFormData ? (
      <div style={{ display: 'flex' }}>
        <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
      </div>
      ) : (
      <Box>
        <Box style={{ height: '55vh', overflowY: 'scroll' }}>
          <FormTable columns={columns} rows={rows} />
        </Box>
        <Box className={s.modalButtons} style={{ width: '50%', float: 'right', marginTop: '20px' }}>
          <Button variant="outlined" label="Cancel" onClick={() => closeModal()} />
          <Button variant="contained" label="Continua" onClick={() => {
            formik.handleSubmit();
          }} />
        </Box>
      </Box>
      )}
    </Modal>
  )
}

export default EditStore
